import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { ROUTES } from '@/shared/constants/routes';
import { rememberReturnPath, takeReturnPath } from '@/shared/lib/return-path';
import { peekPendingInvite } from '@/shared/lib/pending-invite';
import { AuthLoading } from './AuthLoading';

/**
 * Signed-in routes. Someone signed out is sent to sign in with the page they
 * asked for kept, twice over: as router state for the password form, and in
 * sessionStorage for the magic link and Google, which leave the app and come
 * back to a fixed URL. Once signed in, a remembered page is opened instead of
 * wherever that fixed URL leads.
 *
 * The assurance level is checked too: a session at aal1 for someone with a
 * verified authenticator factor (or one the API refused with 401
 * MFA_REQUIRED) is not let in; it goes to the code step on /auth, which then
 * returns to the page asked for.
 */
export function AuthGuard(): JSX.Element {
  const { isLoading, isAuthenticated, mfaRequired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const here = `${location.pathname}${location.search}${location.hash}`;
  // Read when sign-in resolves, not tracked: following every later
  // navigation would replay the remembered page.
  const hereRef = useRef(here);
  hereRef.current = here;

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated || mfaRequired) {
      rememberReturnPath(hereRef.current);
      return;
    }
    // Read once and cleared, so it decides one landing and no later one.
    const returnTo = takeReturnPath();
    if (returnTo && returnTo !== hereRef.current) navigate(returnTo, { replace: true });
  }, [isLoading, isAuthenticated, mfaRequired, navigate]);

  if (isLoading) return <AuthLoading />;
  // The code step lives on the sign-in page (AuthPage shows MfaChallenge).
  if (mfaRequired || !isAuthenticated) return <Navigate to={ROUTES.AUTH} replace state={{ from: here }} />;
  // Back from sign-in or sign-up for an invitation (M-01): straight to it,
  // before any brand screen mounts (which would give a brand-new account a
  // brand of its own first).
  const pendingInvite = peekPendingInvite();
  if (pendingInvite) return <Navigate to={`/invite/${pendingInvite}`} replace />;

  return <Outlet />;
}
